import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserLoyaltyInfoThunk,
  updateUserLoyaltyPointsThunk,
} from "../../../store/thunks/userThunks";
import Modal from "../../../components/common/Modal";
import Button from "../../../components/common/Button";
import Input from "../../../components/common/Input";
import Badge from "../../../components/common/Badge";
import {
  StarIcon,
  TrendingUpIcon,
  GiftIcon,
  UsersIcon,
  CheckCircleIcon,
  ExclamationIcon,
} from "@heroicons/react/outline";
import { notifySuccess, notifyError } from "../../../utils/notification";

const LoyaltyManager = ({ isOpen, onClose, userId, userName }) => {
  const dispatch = useDispatch();
  const { loyaltyInfo, loadingLoyalty } = useSelector((state) => state.user);

  const [pointsToAdd, setPointsToAdd] = useState(0);
  const [pointsToRedeem, setPointsToRedeem] = useState(0);
  const [reason, setReason] = useState("");
  const [activeAction, setActiveAction] = useState(null);

  useEffect(() => {
    if (isOpen && userId) {
      dispatch(getUserLoyaltyInfoThunk(userId));
    }
  }, [isOpen, userId, dispatch]);

  const loyalty = loyaltyInfo?.loyalty;

  const tierColors = {
    REGULAR: "bg-gray-100 text-gray-700 border-gray-300",
    SILVER: "bg-gray-200 text-gray-800 border-gray-400",
    GOLD: "bg-yellow-100 text-yellow-800 border-yellow-400",
    PLATINUM: "bg-purple-100 text-purple-800 border-purple-400",
    VIP: "bg-red-100 text-red-800 border-red-400",
  };

  const tierIcons = {
    REGULAR: "⭐",
    SILVER: "⚪",
    GOLD: "🥇",
    PLATINUM: "💎",
    VIP: "👑",
  };

  const handleAddPoints = async () => {
    if (pointsToAdd <= 0) {
      notifyError("Please enter a valid number of points");
      return;
    }

    if (!reason.trim()) {
      notifyError("Please provide a reason");
      return;
    }

    try {
      await dispatch(
        updateUserLoyaltyPointsThunk({
          userId,
          pointsData: {
            points: pointsToAdd,
            action: "ADD",
            reason,
          },
        })
      );
      notifySuccess(`Added ${pointsToAdd} points successfully`);
      setPointsToAdd(0);
      setReason("");
      setActiveAction(null);
    } catch (error) {
      notifyError("Failed to add points");
    }
  };

  const handleRedeemPoints = async () => {
    if (pointsToRedeem <= 0) {
      notifyError("Please enter a valid number of points");
      return;
    }

    if (pointsToRedeem > loyalty.points) {
      notifyError("Insufficient loyalty points");
      return;
    }

    if (!reason.trim()) {
      notifyError("Please provide a reason");
      return;
    }

    try {
      await dispatch(
        updateUserLoyaltyPointsThunk({
          userId,
          pointsData: {
            points: pointsToRedeem,
            action: "REDEEM",
            reason,
          },
        })
      );
      notifySuccess(`Redeemed ${pointsToRedeem} points successfully`);
      setPointsToRedeem(0);
      setReason("");
      setActiveAction(null);
    } catch (error) {
      notifyError("Failed to redeem points");
    }
  };

  if (loadingLoyalty) {
    return (
      <Modal
        isOpen={isOpen}
        closeModal={onClose}
        title={`Loyalty Manager - ${userName}`}
        isFullScreen={true}
      >
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
            <p className="mt-4 text-gray-600">Loading loyalty information...</p>
          </div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      closeModal={onClose}
      title={
        <div className="flex items-center gap-3">
          <StarIcon className="w-6 h-6 text-yellow-500" />
          <span>Loyalty Manager - {userName}</span>
        </div>
      }
      isFullScreen={true}
    >
      <div className="space-y-6">
        {/* Current Status */}
        <div className="bg-gradient-to-br from-yellow-50 to-orange-50 border-2 border-yellow-300 rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-2xl font-bold text-gray-900">
                {loyalty?.points || 0} Points
              </h3>
              <p className="text-sm text-gray-600 mt-1">Current Balance</p>
            </div>
            <div
              className={`px-6 py-3 rounded-full border-2 ${
                tierColors[loyalty?.membershipTier || "REGULAR"]
              }`}
            >
              <div className="text-center">
                <div className="text-3xl mb-1">
                  {tierIcons[loyalty?.membershipTier || "REGULAR"]}
                </div>
                <p className="text-sm font-bold">
                  {loyalty?.membershipTier || "REGULAR"}
                </p>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="bg-white rounded-lg p-3">
              <p className="text-xs text-gray-600">Points Earned</p>
              <p className="text-xl font-bold text-green-600">
                {loyalty?.pointsEarned || 0}
              </p>
            </div>
            <div className="bg-white rounded-lg p-3">
              <p className="text-xs text-gray-600">Points Redeemed</p>
              <p className="text-xl font-bold text-red-600">
                {loyalty?.pointsRedeemed || 0}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-2 gap-4">
          <button
            onClick={() => setActiveAction("add")}
            className={`p-4 rounded-lg border-2 transition-all ${
              activeAction === "add"
                ? "border-green-500 bg-green-50"
                : "border-gray-200 hover:border-green-300"
            }`}
          >
            <TrendingUpIcon className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <p className="font-semibold text-gray-900">Add Points</p>
          </button>

          <button
            onClick={() => setActiveAction("redeem")}
            className={`p-4 rounded-lg border-2 transition-all ${
              activeAction === "redeem"
                ? "border-blue-500 bg-blue-50"
                : "border-gray-200 hover:border-blue-300"
            }`}
          >
            <GiftIcon className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <p className="font-semibold text-gray-900">Redeem Points</p>
          </button>
        </div>

        {/* Add Points Form */}
        {activeAction === "add" && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <TrendingUpIcon className="w-5 h-5 text-green-600" />
              Add Loyalty Points
            </h4>
            <div className="space-y-3">
              <Input
                label="Points to Add"
                type="number"
                value={pointsToAdd}
                onChange={(e) => setPointsToAdd(Number(e.target.value))}
                placeholder="Enter points"
                min="1"
              />
              <Input
                label="Reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Bonus for large purchase"
              />
              <div className="flex gap-2">
                <Button
                  variant="primary"
                  onClick={handleAddPoints}
                  disabled={!pointsToAdd || !reason}
                  className="flex-1"
                >
                  <CheckCircleIcon className="w-4 h-4 mr-2" />
                  Add Points
                </Button>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setActiveAction(null);
                    setPointsToAdd(0);
                    setReason("");
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Redeem Points Form */}
        {activeAction === "redeem" && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <GiftIcon className="w-5 h-5 text-blue-600" />
              Redeem Loyalty Points
            </h4>

            {loyalty?.points > 0 ? (
              <div className="space-y-3">
                <Input
                  label="Points to Redeem"
                  type="number"
                  value={pointsToRedeem}
                  onChange={(e) => setPointsToRedeem(Number(e.target.value))}
                  placeholder="Enter points"
                  min="1"
                  max={loyalty.points}
                />
                <p className="text-sm text-gray-600">
                  Available: {loyalty.points} points
                </p>
                <Input
                  label="Reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder="e.g., Discount on purchase"
                />
                <div className="flex gap-2">
                  <Button
                    variant="primary"
                    onClick={handleRedeemPoints}
                    disabled={!pointsToRedeem || !reason}
                    className="flex-1"
                  >
                    <CheckCircleIcon className="w-4 h-4 mr-2" />
                    Redeem Points
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      setActiveAction(null);
                      setPointsToRedeem(0);
                      setReason("");
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            ) : (
              <div className="text-center py-4">
                <ExclamationIcon className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-600">No points available to redeem</p>
              </div>
            )}
          </div>
        )}

        {/* Referral Information */}
        {loyalty?.referrals && (
          <div className="bg-white border border-gray-200 rounded-lg p-4">
            <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
              <UsersIcon className="w-5 h-5 text-purple-600" />
              Referral Program
            </h4>

            <div className="space-y-3">
              {loyalty.referrals.referralCode && (
                <div className="bg-purple-50 rounded-lg p-3">
                  <p className="text-sm text-gray-600 mb-1">Referral Code</p>
                  <p className="text-2xl font-mono font-bold text-purple-700">
                    {loyalty.referrals.referralCode}
                  </p>
                </div>
              )}

              <div className="grid grid-cols-2 gap-3">
                <div className="text-center p-3 bg-gray-50 rounded-lg">
                  <p className="text-2xl font-bold text-gray-900">
                    {loyalty.referrals.totalReferrals || 0}
                  </p>
                  <p className="text-sm text-gray-600">Total Referrals</p>
                </div>
                <div className="text-center p-3 bg-green-50 rounded-lg">
                  <p className="text-2xl font-bold text-green-600">
                    {loyalty.referrals.successfulReferrals || 0}
                  </p>
                  <p className="text-sm text-gray-600">Successful</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tier Benefits Info */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-4">
          <h4 className="font-semibold text-gray-800 mb-3">Membership Tiers</h4>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span>⭐ REGULAR</span>
              <span className="text-gray-600">Standard benefits</span>
            </div>
            <div className="flex items-center justify-between">
              <span>⚪ SILVER</span>
              <span className="text-gray-600">2x points on purchases</span>
            </div>
            <div className="flex items-center justify-between">
              <span>🥇 GOLD</span>
              <span className="text-gray-600">
                3x points + priority service
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>💎 PLATINUM</span>
              <span className="text-gray-600">
                4x points + exclusive offers
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span>👑 VIP</span>
              <span className="text-gray-600">
                5x points + personal consultant
              </span>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default LoyaltyManager;
